import { Box, Container, Typography, Stack, Grid, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from '../components/SEOHead';
import SchoolIcon from '@mui/icons-material/School';
import BalanceIcon from '@mui/icons-material/Balance';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PeopleIcon from '@mui/icons-material/People';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import CelebrationIcon from '@mui/icons-material/Celebration';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmailIcon from '@mui/icons-material/Email';

const PageSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(12, 0),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 0),
  },
}));

const GlassCard = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.05)'
    : 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  padding: theme.spacing(4),
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 32px rgba(0, 0, 0, 0.3)'
    : '0 8px 32px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 12px 48px rgba(0, 0, 0, 0.4)'
      : '0 12px 48px rgba(0, 0, 0, 0.15)',
  },
}));

export default function Career() {
  const { t } = useLanguage();

  const culture = [
    {
      icon: <SchoolIcon sx={{ fontSize: 48 }} />,
      title: t('career.culture.learning'),
      desc: t('career.culture.learning.desc'),
    },
    {
      icon: <BalanceIcon sx={{ fontSize: 48 }} />,
      title: t('career.culture.balance'),
      desc: t('career.culture.balance.desc'),
    },
    {
      icon: <LightbulbIcon sx={{ fontSize: 48 }} />,
      title: t('career.culture.innovation'),
      desc: t('career.culture.innovation.desc'),
    },
    {
      icon: <PeopleIcon sx={{ fontSize: 48 }} />,
      title: t('career.culture.team'),
      desc: t('career.culture.team.desc'),
    },
  ];

  const benefits = [
    { icon: <AttachMoneyIcon />, text: t('career.benefits.competitive') },
    { icon: <LocalHospitalIcon />, text: t('career.benefits.health') },
    { icon: <SchoolIcon />, text: t('career.benefits.training') },
    { icon: <HomeWorkIcon />, text: t('career.benefits.remote') },
    { icon: <CelebrationIcon />, text: t('career.benefits.social') },
    { icon: <TrendingUpIcon />, text: t('career.benefits.career') },
  ];

  return (
    <>
      <SEOHead titleKey="career.title" descriptionKey="career.intro.desc" />
      <Box component="main" role="main">
        {/* Hero Section */}
        <PageSection
          sx={{
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'
                : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            pt: { xs: 16, md: 20 },
          }}
        >
          <Container maxWidth="lg">
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 700,
                  background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('career.title')}
              </Typography>
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ maxWidth: '800px', fontSize: '1.25rem' }}
              >
                {t('career.subtitle')}
              </Typography>
            </Stack>
          </Container>
        </PageSection>

        {/* Intro Section */}
        <PageSection>
          <Container maxWidth="lg">
            <GlassCard sx={{ textAlign: 'center', py: 6 }}>
              <Stack spacing={3} alignItems="center">
                <Typography variant="h3" fontWeight={700}>
                  {t('career.intro.title')}
                </Typography>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ maxWidth: '800px', lineHeight: 1.8, fontSize: '1.1rem' }}
                >
                  {t('career.intro.desc')}
                </Typography>
              </Stack>
            </GlassCard>
          </Container>
        </PageSection>

        {/* Culture Section */}
        <PageSection
          sx={{
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.02)'
                : 'rgba(0, 0, 0, 0.02)',
          }}
        >
          <Container maxWidth="lg">
            <Stack spacing={6}>
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Typography variant="h2" fontWeight={700}>
                  {t('career.culture.title')}
                </Typography>
              </Stack>
              <Grid container spacing={4}>
                {culture.map((item, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <GlassCard sx={{ height: '100%' }}>
                      <Stack spacing={2} alignItems="center" textAlign="center">
                        <Box
                          sx={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: (theme) =>
                              `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.secondary.main}20 100%)`,
                            color: 'primary.main',
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem' }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                          {item.desc}
                        </Typography>
                      </Stack>
                    </GlassCard>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Container>
        </PageSection>

        {/* Benefits Section */}
        <PageSection>
          <Container maxWidth="lg">
            <Stack spacing={6}>
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Typography variant="h2" fontWeight={700}>
                  {t('career.benefits.title')}
                </Typography>
              </Stack>
              <Grid container spacing={3}>
                {benefits.map((benefit, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <GlassCard>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box
                          sx={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: (theme) =>
                              `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                            color: 'white',
                          }}
                        >
                          {benefit.icon}
                        </Box>
                        <Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.1rem' }}>
                          {benefit.text}
                        </Typography>
                      </Stack>
                    </GlassCard>
                  </Grid>
                ))}
              </Grid>
            </Stack>
          </Container>
        </PageSection>

        {/* Open Positions Section */}
        <PageSection
          sx={{
            background: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.02)'
                : 'rgba(0, 0, 0, 0.02)',
          }}
        >
          <Container maxWidth="lg">
            <Stack spacing={6}>
              <Stack spacing={2} alignItems="center" textAlign="center">
                <Typography variant="h2" fontWeight={700}>
                  {t('career.positions.title')}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                  {t('career.positions.desc')}
                </Typography>
              </Stack>
              <GlassCard sx={{ textAlign: 'center', py: 8 }}>
                <Stack spacing={3} alignItems="center">
                  <EmailIcon sx={{ fontSize: 80, color: 'primary.main' }} />
                  <Typography variant="h4" fontWeight={700}>
                    {t('career.apply.title')}
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ maxWidth: '600px', fontSize: '1.1rem' }}
                  >
                    {t('career.apply.desc')}
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    href="mailto:kariyer@teknolobi.com"
                    sx={{
                      px: 6,
                      py: 2,
                      fontSize: '1.1rem',
                      borderRadius: '12px',
                      background: (theme) =>
                        `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: (theme) =>
                          theme.palette.mode === 'dark'
                            ? '0 12px 40px rgba(0, 0, 0, 0.4)'
                            : '0 12px 40px rgba(0, 0, 0, 0.2)',
                      },
                    }}
                  >
                    {t('career.cta')}
                  </Button>
                </Stack>
              </GlassCard>
            </Stack>
          </Container>
        </PageSection>
      </Box>
    </>
  );
}